import React, { useMemo } from 'react';
import { Box } from '@withjoy/joykit';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { StationeryProductSelector } from '../ProductDetailsPage.types';
import { useSaleColor } from '../../DesignsGallery/utils/useSaleColor';
import { useProductToUIMapping } from '@apps/card/components/ProductUIConfig/useProductToUIMapping';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { useGetDiscountByVendor } from '@apps/card/hooks/useGetDiscountByVendor';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import { formatProductSizeParts, useCardSizeLabels } from '@apps/card/utils/cardSizeName';
import { usePrintPrices } from '@apps/card/hooks/usePrintPrices';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { convertProductTypeToFormatParam } from '../utils';
import { usePdpColorways } from '../PdpColorwayContext';
import { useStationeryTemplatesForProductSizePickerQuery } from './ProductSizeSelector.graphql.generated';
import type { ProductSizeOption } from './productSizeOptions.types';
import { buildProductSizeOptions } from './productSizeOptions';
import type { ProductPickerGroup, ProductPickerGroupSale, ProductPickerRow } from '@apps/card/components/ProductPicker/ProductPicker.types';
import { groupOfRow, ProductPicker, ProductPickerPrice } from '@apps/card/components/ProductPicker/ProductPicker';
import { getProductIconKind } from '@apps/card/components/ProductPicker/ProductIcon';

export const ProductSizeSelector = (props: { productSelector: StationeryProductSelector; templateCategory: StationeryTemplateCategoryEnum; themeId: string }) => {
  const { productSelector, templateCategory, themeId } = props;
  const { currentTemplate } = productSelector;
  const { getProductWithUIMapping } = useProductToUIMapping();
  const { products: currentProducts, activeProduct } = useStationeryProductPricingContext();
  const sizeLabels = useCardSizeLabels();
  const minorUnitsToPriceString = useMinorUnitsToPriceString();
  const { getDiscountByVendor } = useGetDiscountByVendor();
  const { buttonInteracted } = useCardTelemetry({ page: 'productPdpModal' });
  const { goToProductDetailsPage } = useCardsRouterContext();
  const { selectedColorwayId } = usePdpColorways();
  const saleColor = useSaleColor();

  const { data, loading } = useStationeryTemplatesForProductSizePickerQuery({
    variables: {
      filter: {
        categories: [templateCategory],
        enabled: true,
        side: 'front',
        themeIds: [themeId],
      },
    },
    batchMode: 'fast',
    skip: !currentTemplate,
    // The size options only serve the interactive picker — no SEO value, so
    // don't block server rendering on them.
    ssr: false,
  });

  const options = useMemo<ProductSizeOption[]>(
    () =>
      buildProductSizeOptions({
        templateCategory,
        currentTemplate,
        currentProducts,
        themeTemplates: data?.stationeryTemplatesContainer.pagedResults.edges.map(edge => edge.node) ?? [],
        mapProduct: getProductWithUIMapping,
      }),
    [currentProducts, currentTemplate, data, getProductWithUIMapping, templateCategory],
  );

  // Options can span templates beyond the current one, so pricing needs a
  // calculator over every option's product, not the context's (current
  // template only) calculator.
  const optionProducts = useMemo(() => {
    const byId = new Map<string, StationeryProduct>();
    options.forEach(o => byId.set(o.product.id, o.product));
    return [...byId.values()];
  }, [options]);
  const priceCalculator = usePrintPrices(optionProducts);

  const currentKey = `${currentTemplate?.id ?? ''}:${activeProduct.id}`;

  const handleSelect = (key: string) => {
    const option = options.find(o => o.key === key);
    if (!option || key === currentKey) return;
    if (!currentTemplate || option.templateId === currentTemplate.id) {
      // Same template: a plain product switch, handled (and instrumented) by the parent.
      productSelector.onChange(option.product.id);
      return;
    }
    // A digital row borrowed from a sibling template is a product switch that
    // happens to hop templates, not a size choice — digital has no size.
    if (option.product.cardTypeSKU) {
      buttonInteracted({
        name: 'pdpModal_sizeControlClicked',
        themeId,
        stationeryTemplateCategory: templateCategory,
        fromTemplateId: currentTemplate.id,
        toTemplateId: option.templateId,
        fromCardWidthInches: currentTemplate.widthInches,
        fromCardHeightInches: currentTemplate.heightInches,
        toCardWidthInches: option.widthInches,
        toCardHeightInches: option.heightInches,
      });
    }
    if (option.product.productType !== activeProduct.productType) {
      buttonInteracted({ name: 'pdpModal_formatControlClicked', themeId, format: option.product.id });
    }
    // Carry the colorway too — the navigation replaces the query string, so an
    // omitted param resets the couple's selection.
    goToProductDetailsPage('wedding', templateCategory, themeId, option.templateId, 'replace', convertProductTypeToFormatParam(option.product.productType), selectedColorwayId);
  };

  if (loading && !data && currentTemplate) {
    return (
      <Box marginBottom={7}>
        <ProductPicker.Skeleton />
      </Box>
    );
  }

  if (options.length === 0) {
    return null;
  }

  // A row's discount is only the one its price actually shows: a configured
  // sale that rounds to the same unit price (free digital, say) earns neither a
  // strikethrough nor a savings chip.
  const rows = options.map(option => {
    const { product } = option;
    const discount = getDiscountByVendor(product.vendor);
    const priceData = priceCalculator.getProductBasePrice(product.id, product.defaultQuantity, discount, true);
    const unitPriceString = minorUnitsToPriceString(priceData.unitPriceInMinorUnits, { canShowFreeLabel: true });
    const preDiscountUnitPriceString = priceData.discounts && minorUnitsToPriceString(priceData.discounts.preDiscountedUnitPriceInMinorUnits, { canShowFreeLabel: true });
    const shownDiscount = discount && preDiscountUnitPriceString && preDiscountUnitPriceString !== unitPriceString ? discount : undefined;
    const sizedOn = { category: templateCategory, widthInches: option.widthInches, heightInches: option.heightInches };
    const title = formatProductSizeParts(templateCategory, option.widthInches, option.heightInches, sizeLabels, product);

    const row: ProductPickerRow = {
      key: option.key,
      productType: product.productType,
      icon: getProductIconKind(product, sizedOn),
      name: title.name,
      size: title.size,
      price: (
        <ProductPickerPrice
          price={unitPriceString}
          originalPrice={shownDiscount ? preDiscountUnitPriceString : undefined}
          isFree={priceData.unitPriceInMinorUnits === 0}
          saleColor={saleColor}
        />
      ),
    };
    return { row, discount: shownDiscount };
  });

  // Each group carries the sale its own rows are priced at — printed and
  // digital can run different codes at once — so the chip and the code a
  // shopper is told to use always belong to the rows beside them. The sale
  // config stores the rate as a fraction (0.15); the chip reads whole percent.
  const saleFor = (group: ProductPickerGroup): ProductPickerGroupSale | undefined => {
    const discount = rows.find(({ row, discount: rowDiscount }) => rowDiscount && groupOfRow(row) === group)?.discount;
    return discount ? { savingsPercentage: Math.round(discount.percentage * 100), promoCode: discount.promoCode } : undefined;
  };

  return (
    <Box marginBottom={7}>
      <ProductPicker
        rows={rows.map(({ row }) => row)}
        value={currentKey}
        onSelect={handleSelect}
        saleByGroup={{ printed: saleFor('printed'), digital: saleFor('digital') }}
        saleColor={saleColor}
      />
    </Box>
  );
};
